/**
 * This is one of the Standard Widgets in the Web Widgets Framework. It uses a custom template compared
 * to DIJIT's Menu
 * MenuPanel widget is a container widget and List Items are supposed to be used with in a Menu.
 * So, a MenuPanel must be created first and then list item's should be added to the Menu.<br>
 * This widget should be used to obtain a scrollable area with in the Menu
 * To see all the available properties for this widget, please look at the corresponding Property Mixins below.
 * <br>
 * <br>
 * <strong>Property Mixins:</strong><br>
 * {@link module:mw-form/mixins/property/MaxHeightMixin}<br>
 * {@link module:mw-form/mixins/property/VisualFamilyMixin}<br>
 * {@link module:mw-form/mixins/property/TagMixin}<br>
 *
 * @example <caption>Creating MenuPanel</caption>
 * var MenuPanel1 = new MenuPanel({
       maxHeight: 200
 * });
 *
 *
 * MenuPanel1.placeAt("someId");
 * MenuPanel1.startup();
 *
 * @module mw-form/MenuPanel
 *
 * @copyright Copyright 2014-2019 The MathWorks, Inc.
 */

define([
    "dojo/_base/declare",
    "dijit/_WidgetBase",
    "dijit/_TemplatedMixin",
    "dijit/_Container", /* No need of container mixin as keyNavContainer mixin extends container */
    "dijit/registry",
    "mw-form/mixins/MenuKeyNavMixin", /* Custom key Navigation */
    "mw-form/mixins/ContainerFocusMixin",
    "mw-form/mixins/MenuFocusMixin",
    "mw-form/mixins/PreventFocusMixin",
    "mw-form/mixins/property/MaxHeightMixin",
    "mw-form/mixins/property/TagMixin",
    "mw-form/mixins/property/VisualFamilyMixin",
    "mw-form/mixins/mixinDependencyValidator",
    "dojo/text!./templates/MenuPanel.html"
], function (declare, _WidgetBase, _TemplatedMixin, _Container, registry, KeyNavMixin, ContainerFocusMixin,
             MenuFocusMixin, PreventFocusMixin, MaxHeightMixin, TagMixin, VisualFamilyMixin,
             mixinDependencyValidator, template) {

    return declare(mixinDependencyValidator.validate([_WidgetBase, _Container, KeyNavMixin,
        ContainerFocusMixin, MenuFocusMixin, PreventFocusMixin, _TemplatedMixin, TagMixin, MaxHeightMixin,
        VisualFamilyMixin]), {
        baseClass: "mwWidget mwMenuPanel",
        templateString: template,
        maxHeight: 100,

        postCreate: function () {
            this.inherited(arguments);
            
            var getRelativeNodes = function (/* Event? */ e) {
                relativeNodes = {};
                
                if (this.getParent()) {
                    relativeNodes.parent = this.getParent();
                    
                    if (relativeNodes.parent._lastDirection) {
                        relativeNodes.direction = relativeNodes.parent._lastDirection;
                    }
                    
                    if (this.getParent()._findFocusableChild) {
                        relativeNodes.nextNode = relativeNodes.parent._findFocusableChild(this.domNode, false, "next");
                        relativeNodes.previousNode = relativeNodes.parent._findFocusableChild(this.domNode, false, "previous");
                    } else if (this._findFocusableChild && this.domNode.contains(document.activeElement)) {
                        relativeNodes.nextNode = this._findFocusableChild(document.activeElement, false, "next");
                        relativeNodes.previousNode = this._findFocusableChild(document.activeElement, false, "previous");
                    } else {
                        relativeNodes.nextNode = this.domNode.nextElementSibling;
                        relativeNodes.previousNode = this.domNode.previousElementSibling;
                    }
                } else {
                    relativeNodes.nextNode = this.domNode.nextElementSibling;
                    relativeNodes.previousNode = this.domNode.previousElementSibling;
                }

                // if nextNode is an empty MenuPanel, it should not take focus
                if(relativeNodes.nextNode && relativeNodes.nextNode.classList.contains("mwMenuPanel") && relativeNodes.nextNode.childElementCount === 0) {
                    relativeNodes.nextNode = null;
                }
                // if previousNode is an empty MenuPanel, it should not take focus
                if(relativeNodes.previousNode && relativeNodes.previousNode.classList.contains("mwMenuPanel") && relativeNodes.previousNode.childElementCount === 0) {
                    relativeNodes.previousNode = null;
                }
                
                if(this.domNode.childElementCount !== 0 ) { // only if the MenuPanel is NOT empty
                    if (this._findFocusableChild) {
                        relativeNodes.firstNode = this._findFocusableChild(this.domNode, false, "next");
                        relativeNodes.lastNode = this._findFocusableChild(this.domNode, false, "previous");
                    } else {
                        relativeNodes.firstNode = this.domNode.firstElementChild;
                        relativeNodes.lastNode = this.domNode.lastElementChild;
                    }

                    // first and last node cannot be the MenuPanel itself
                    if (relativeNodes.firstNode === this.domNode) {
                        relativeNodes.firstNode = null;
                    }
                    if (relativeNodes.lastNode === this.domNode) {
                        relativeNodes.lastNode = null;
                    }                    
                }
                
                if (e && e.target) {
                    relativeNodes.fromNode = e.target;
                } else {
                    relativeNodes.fromNode = document.activeElement;
                }
                
                return relativeNodes;
            }.bind(this);

            this._focusHandler = function (e) {
                var relativeNodes, direction, firstNode, lastNode, nextNode, previousNode, fromNode;

                if ((e.target === this.domNode) && this.getParent()) {
                    relativeNodes = getRelativeNodes(e);
                    direction = relativeNodes.direction;
                    firstNode = relativeNodes.firstNode; // first node in the MenuPanel
                    lastNode = relativeNodes.lastNode; // last node in the MenuPanel
                    nextNode = relativeNodes.nextNode; // node that is right below the MenuPanel
                    previousNode = relativeNodes.previousNode; // node that is right above the MenuPanel

                    if (direction) {
                        switch (direction) {
                            case "up":
                                // If lastNode exists focus that, otherwise focus the node that is right above the MenuPanel.
                                // If there's no focusable node, focus the parent menu
                                if (lastNode) {
                                    lastNode.focus();
                                } else if (previousNode) {
                                    previousNode.focus();
                                } else {
                                    relativeNodes.parent.focus();
                                }
                                break;
                            case "down":
                                // If firstNode exists focus that, otherwise focus the node that is right below the MenuPanel
                                // If there's no focusable node, focus the parent menu
                                if (firstNode) {
                                    firstNode.focus();
                                } else if (nextNode) {
                                    nextNode.focus();
                                } else {
                                    relativeNodes.parent.focus();
                                }
                                break;
                            case "top":
                                // If firstNode exists focus that, otherwise focus the first node in parent container
                                // If there's no focusable node, focus the parent menu
                                if (firstNode) {
                                    firstNode.focus();
                                } else if (nextNode) {
                                    // if nextNode exists, it is guaranteed that there's atleast one next-focusable node in parent container  
                                    relativeNodes.parent.focusNext();
                                } else {
                                    relativeNodes.parent.focus();
                                }
                                break;
                            case "bottom":
                                // If lastNode exists focus that, otherwise focus last node in parent container
                                // If there's no focusable node, focus the parent menu
                                if (lastNode) {
                                    lastNode.focus();
                                } else if (previousNode) {
                                    // if previousNode exists, it is guaranteed that there's atleast one previous-focusable node in parent container
                                    relativeNodes.parent.focusPrevious();
                                } else {
                                    relativeNodes.parent.focus();
                                }
                                break;
                            default:
                                firstNode.focus();
                        }
                    } else {
                        fromNode = relativeNodes.fromNode;
                        
                        if (fromNode === previousNode) {
                            firstNode.focus();
                        } else if (fromNode === nextNode) {
                            lastNode.focus();
                        } else {
                            // Home, Page Up
                            if (this.getParent()._findFocusableChild() === this.domNode) {
                                firstNode.focus();
                            } else { // End, Page Down
                                lastNode.focus();
                            }
                        }
                    }
                }
            }.bind(this);

            this._keyDownHandler = function (e) {
                var relativeNodes, nextnode, previousNode, firstNode, lastNode;
                
                if (this.getParent()) {
                    relativeNodes = getRelativeNodes(e);
                    
                    nextNode = relativeNodes.nextNode;
                    previousNode = relativeNodes.previousNode;
                    firstNode = relativeNodes.firstNode;
                    lastNode = relativeNodes.lastNode;

                    if ((e.keyCode === 40) && (e.target === lastNode)) { //DOWN_ARROW
                        this.getParent()._lastDirection = "down";
                        nextNode.focus();
                        e.preventDefault(); //Prevent scrolling
                        e.stopImmediatePropagation(); //Prevent parent menu handling event
                    } else if ((e.keyCode === 40) && !this.getParent()._findFocusableChild) { //DOWN_ARROW
                        nextNode.focus();
                        e.preventDefault(); //Prevent scrolling
                    } else if ((e.keyCode === 38) && (e.target === firstNode)) { //UP_ARROW
                        this.getParent()._lastDirection = "up";
                        previousNode.focus();
                        e.preventDefault(); //Prevent scrolling
                        e.stopImmediatePropagation(); //Prevent parent menu handling event
                    } else if ((e.keyCode === 38) && !this.getParent()._findFocusableChild) { //UP_ARROW
                        previousNode.focus();
                        e.preventDefault(); //Prevent scrolling
                    } else if (e.keyCode === 9) { //TAB
                        if (e.shiftKey && (e.target === firstNode)) {
                            this.getParent()._lastDirection = "up";
                            previousNode.focus();
                            e.preventDefault(); //Prevent scrolling
                            e.stopImmediatePropagation(); //Prevent parent menu handling event
                        } else if (!e.shiftKey && (e.target === lastNode)) {
                            this.getParent()._lastDirection = "down";
                            nextNode.focus();
                            e.preventDefault(); //Prevent scrolling
                            e.stopImmediatePropagation(); //Prevent parent menu handling event
                        }
                    }
                    // } else { /*Allow Parent menu to handle key nav*/ }
                }
            }.bind(this);

            this.domNode.addEventListener("focus", this._focusHandler, true); // Do not want the bubbling behavior, so focus
            this.domNode.addEventListener("keydown", this._keyDownHandler, true);
            this.domNode.addEventListener("synkeydown", this._keyDownHandler, true);
        },

        // Forward any close requests to parent
        closeRequest: function (focusParent) {
            var parent = this._getParentWidget();
            if (parent != null && parent.closeRequest) {
                if (parent._closeEventData) {
					if (parent._closeEventData.mwEventData) {
                    	parent._closeEventData.mwEventData.gainedFocusWidget = null;
					} else {
        	            parent._closeEventData.mwEventData = {
    	                    gainedFocusWidget: null
	                    };
					}
                } else {
                    parent._closeEventData = {mwEventData: {
                        gainedFocusWidget: null
                    }};
                }
                
                parent.closeRequest(focusParent ? focusParent : false);
            }
        },

        _focusChange: function (gainedFocusWidget) {
            var parent = this._getParentWidget();
            if (parent != null && parent._focusChange) {
                if (parent._closeEventData) {
					if (parent._closeEventData.mwEventData) {
                    	parent._closeEventData.mwEventData.gainedFocusWidget = gainedFocusWidget;
					} else {
        	            parent._closeEventData.mwEventData = {
    	                    gainedFocusWidget: gainedFocusWidget
	                    };
					}
                } else {
                    parent._closeEventData = {mwEventData: {
                        gainedFocusWidget: gainedFocusWidget
                    }};
                }
                parent._focusChange(gainedFocusWidget);
            }
        },

        _getParentWidget: function () {
            return registry.getEnclosingWidget(this.domNode.parentNode);
        },

        destroy: function () {
            if (this.domNode) {
                this.domNode.removeEventListener("focus", this._focusHandler, true);
                this.domNode.removeEventListener("keydown", this._keyDownHandler, true);
                this.domNode.removeEventListener("synkeydown", this._keyDownHandler, true);
            }
            this.inherited(arguments);
        }
    });

});